import { useState } from "react";
import { Link } from "react-router-dom";
import { Anchor, EnvelopeSimple, MapPin, Phone, LinkedinLogo, FacebookLogo, InstagramLogo } from "@phosphor-icons/react";
import api from "../lib/api";
import { submitToFormspree } from "../lib/formspree";
import { toast } from "sonner";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            const [fsRes, dbRes] = await Promise.allSettled([
                submitToFormspree({ _subject: "Windermere · Newsletter Subscription", _form: "newsletter", email }),
                api.post("/newsletter", { email }),
            ]);
            const ok = (fsRes.status === "fulfilled" && fsRes.value) || dbRes.status === "fulfilled";
            if (ok) {
                toast.success(dbRes?.value?.data?.message || "Subscribed to Windermere insights.");
                setEmail("");
            } else {
                toast.error("Could not subscribe");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <footer data-testid="site-footer" className="relative bg-[#040914] border-t border-white/5 pb-24 lg:pb-8">
            <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#48CAE4]/50 to-transparent" />

            <div className="wms-container pt-20 pb-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    {/* Brand + newsletter */}
                    <div className="md:col-span-5">
                        <div className="flex items-center gap-4">
                            <span className="flex items-center justify-center w-32 h-32 bg-white p-2 shadow-[0_4px_24px_rgba(72,202,228,0.18)]">
                                <img
                                    src="https://customer-assets.emergentagent.com/job_windermere-shipping/artifacts/1re155dq_Minimalist%20Logo%20Design%20for%20Windermere%20Shipping%20Pte.png"
                                    alt="Windermere Shipping Pte Ltd logo"
                                    className="w-full h-full object-contain"
                                />
                            </span>
                            <div className="leading-none">
                                <div className="font-display text-base tracking-[0.18em] uppercase">Windermere Shipping</div>
                                <div className="text-[10px] tracking-[0.32em] uppercase text-[#48CAE4]/80 mt-1">PTE LTD · Singapore</div>
                            </div>
                        </div>
                        <p className="mt-6 max-w-md text-[#94A3B8] leading-relaxed text-[15px]">
                            A Singapore-headquartered marine transportation and chartering company providing
                            global vessel chartering, marine logistics, shipbuilding and offshore support
                            to industrial operators worldwide.
                        </p>

                        <form onSubmit={submit} className="mt-8" data-testid="newsletter-form">
                            <label className="wms-overline">Insights Brief</label>
                            <div className="mt-3 flex border border-white/10 bg-[#0B1324]">
                                <input
                                    type="email"
                                    required
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="your@company.com"
                                    data-testid="newsletter-email-input"
                                    className="flex-1 bg-transparent px-4 py-3 text-sm text-white placeholder-white/30 outline-none"
                                />
                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="newsletter-submit-btn"
                                    className="px-5 text-xs uppercase tracking-[0.22em] bg-white text-[#040914] hover:bg-[#48CAE4] transition-colors disabled:opacity-60"
                                >
                                    {loading ? "..." : "Subscribe"}
                                </button>
                            </div>
                        </form>
                    </div>

                    <div className="md:col-span-3">
                        <div className="wms-overline">Navigate</div>
                        <ul className="mt-5 space-y-3 text-[15px] text-white/70">
                            {[
                                ["Home", "/"], ["About", "/about"], ["Services", "/services"],
                                ["Fleet", "/fleet"], ["Careers", "/careers"], ["Contact", "/contact"],
                            ].map(([label, to]) => (
                                <li key={to}>
                                    <Link to={to} className="hover:text-[#48CAE4] transition-colors" data-testid={`footer-link-${label.toLowerCase()}`}>
                                        {label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="md:col-span-4">
                        <div className="wms-overline">Headquarters</div>
                        <ul className="mt-5 space-y-4 text-[15px] text-white/70">
                            <li className="flex items-start gap-3">
                                <MapPin weight="duotone" size={20} className="text-[#48CAE4] shrink-0 mt-0.5" />
                                <span>1 Kim Seng Promenade<br />#16-07/08 Great World City<br />Singapore 237994</span>
                            </li>
                            <li className="flex items-center gap-3">
                                <EnvelopeSimple weight="duotone" size={20} className="text-[#48CAE4]" />
                                <a href="mailto:chartering@windermereinland.com" className="hover:text-white">chartering@windermereinland.com</a>
                            </li>
                            <li className="flex items-center gap-3">
                                <Phone weight="duotone" size={20} className="text-[#48CAE4]" />
                                <a href="tel:+6583373376" className="hover:text-white">+65 8337 3376</a>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="mt-16 pt-6 border-t border-white/5 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <p className="text-xs tracking-[0.18em] uppercase text-white/40">
                        © {new Date().getFullYear()} Windermere Shipping Pte Ltd · UEN 2025XXXXXX · All rights reserved
                    </p>
                    <div className="flex items-center gap-3">
                        {[
                            { Icon: LinkedinLogo, label: "linkedin" },
                            { Icon: FacebookLogo, label: "facebook" },
                            { Icon: InstagramLogo, label: "instagram" },
                        ].map(({ Icon, label }) => (
                            <a
                                key={label}
                                href="#"
                                aria-label={label}
                                data-testid={`social-${label}`}
                                className="w-9 h-9 inline-flex items-center justify-center border border-white/10 text-white/60 hover:text-white hover:border-white/30 transition"
                            >
                                <Icon size={16} weight="duotone" />
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        </footer>
    );
}
